// src/pages/PrivacyPolicy.jsx - NEVXOAULIO VENTURES PRIVACY POLICY
import React from 'react';
import { Mail, Shield, Eye, Lock, UserCheck, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Introduction */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Eye className="w-6 h-6 text-blue-600" />
            Introduction
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Nevxoaulio Ventures ("Company", "we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
            Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our services. By accessing and using Nevxoaulio Ventures' website and services, you acknowledge that you have read, understood, and agree to be bound by all the provisions of this Privacy Policy.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-blue-600" />
            Information We Collect
          </h2>

          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-gray-800 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Personal Information You Provide</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>Name, email address, phone number, and mailing address</li>
                <li>Company name and job title</li>
                <li>Payment information (credit card, banking details)</li>
                <li>Service-related information and preferences</li>
                <li>Resume/CV information for career applications</li>
                <li>Feedback, comments, and messages you send to us</li>
                <li>Any other information you voluntarily provide</li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-gray-800 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Automatically Collected Information</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>IP address and geolocation data</li>
                <li>Browser type, version, and language preferences</li>
                <li>Device type, operating system, and unique device identifiers</li>
                <li>Pages visited, time spent on pages, and referral sources</li>
                <li>Clickstream data and interaction patterns</li>
                <li>Cookies, web beacons, and similar tracking technologies</li>
                <li>Session identification data</li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-gray-800 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Third-Party Information</h3>
              <p className="text-gray-700 dark:text-gray-300">
                We may receive information about you from third-party sources including business partners, service providers, payment processors, and publicly available sources.
              </p>
            </div>
          </div>
        </section>

        {/* How We Use Information */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
              <h3 className="font-semibold mb-3 text-lg">Service Delivery</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>Process transactions and send related information</li>
                <li>Provide technical support and customer service</li>
                <li>Develop new products and services</li>
                <li>Improve user experience and website functionality</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
              <h3 className="font-semibold mb-3 text-lg">Communication</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>Send marketing emails and promotional content</li>
                <li>Send service updates and important notices</li>
                <li>Respond to your inquiries and feedback</li>
                <li>Send surveys and request your opinions</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
              <h3 className="font-semibold mb-3 text-lg">Analytics & Research</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>Analyze usage patterns and user behavior</li>
                <li>Generate statistical analysis and reports</li>
                <li>Conduct market research and surveys</li>
                <li>Personalize your experience</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
              <h3 className="font-semibold mb-3 text-lg">Legal & Security</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>Detect and prevent fraud</li>
                <li>Enforce terms of service</li>
                <li>Comply with legal obligations</li>
                <li>Protect rights and safety</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Data Sharing */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Data Sharing & Disclosure</h2>

          <div className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              <span className="font-semibold">Service Providers:</span> We share information with third-party service providers who perform services on our behalf, including payment processors, analytics providers, and hosting services.
            </p>
            <p>
              <span className="font-semibold">Business Partners:</span> We may share information with partners for joint marketing initiatives and co-branded services.
            </p>
            <p>
              <span className="font-semibold">Legal Requirements:</span> We may disclose information if required by law, court order, or government request.
            </p>
            <p>
              <span className="font-semibold">Business Transfers:</span> In case of merger, acquisition, or bankruptcy, your information may be transferred as part of that transaction.
            </p>
            <p>
              <span className="font-semibold">With Your Consent:</span> We may share information for purposes not listed above with your explicit consent.
            </p>
            <p className="font-semibold text-red-600 dark:text-red-400">
              We do NOT sell or rent your personal information to third parties for marketing purposes.
            </p>
          </div>
        </section>

        {/* Data Security */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Lock className="w-6 h-6 text-blue-600" />
            Data Security
          </h2>

          <div className="bg-blue-50 dark:bg-gray-800 p-6 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We implement industry-standard security measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure authentication mechanisms and access controls</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Firewalls and intrusion detection systems</li>
              <li>Secure data storage and backup procedures</li>
              <li>Employee training on data protection practices</li>
            </ul>
            <p className="mt-4 text-sm text-red-600 dark:text-red-400 font-semibold">
              However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </div>
        </section>

        {/* Cookies & Tracking */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Cookies & Tracking Technologies</h2>

          <div className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              We use cookies, web beacons, pixels, and similar tracking technologies to enhance your user experience and gather analytics data. These tools help us:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Remember your preferences and login information</li>
              <li>Track website usage and user behavior</li>
              <li>Deliver personalized content and advertisements</li>
              <li>Analyze website performance and user demographics</li>
            </ul>
            <p className="mt-4">
              You can control cookies through your browser settings. However, disabling cookies may affect the functionality of our website.
            </p>
          </div>
        </section>

        {/* Your Rights */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Your Privacy Rights</h2>

          <div className="space-y-4 bg-blue-50 dark:bg-gray-800 p-6 rounded-lg">
            <p className="font-semibold text-lg">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li><span className="font-semibold">Access:</span> Request access to your personal information</li>
              <li><span className="font-semibold">Correct:</span> Request correction of inaccurate or incomplete data</li>
              <li><span className="font-semibold">Delete:</span> Request deletion of your personal information (right to be forgotten)</li>
              <li><span className="font-semibold">Portability:</span> Request a copy of your data in a structured format</li>
              <li><span className="font-semibold">Opt-Out:</span> Unsubscribe from marketing communications</li>
              <li><span className="font-semibold">Withdraw Consent:</span> Withdraw previously given consent</li>
              <li><span className="font-semibold">Lodge a Complaint:</span> File a complaint with data protection authorities</li>
            </ul>
          </div>
        </section>

        {/* Retention */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Data Retention</h2>

          <p className="text-gray-700 dark:text-gray-300">
            We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. Retention periods vary based on the type of data and legal requirements:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mt-4">
            <li><span className="font-semibold">Account Information:</span> Retained while your account is active</li>
            <li><span className="font-semibold">Transaction Records:</span> Retained for 7+ years for legal/tax compliance</li>
            <li><span className="font-semibold">Analytics Data:</span> Retained for up to 2 years</li>
            <li><span className="font-semibold">Marketing Communications:</span> Retained until you unsubscribe</li>
          </ul>
        </section>

        {/* Children's Privacy */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>

          <p className="text-gray-700 dark:text-gray-300">
            Our services are not intended for individuals under 13 years old. We do not knowingly collect personal information from children under 13. If we become aware that we have collected information from a child under 13, we will take steps to delete such information immediately and terminate the child's account.
          </p>
        </section>

        {/* International Data Transfer */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">International Data Transfer</h2>

          <p className="text-gray-700 dark:text-gray-300">
            Your information may be transferred to, stored in, and processed in countries other than India. These countries may have data protection laws that differ from India. By using our services, you consent to the transfer of your information to countries outside India.
          </p>
        </section>

        {/* Third-Party Links */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Third-Party Links</h2>

          <p className="text-gray-700 dark:text-gray-300">
            Our website may contain links to third-party websites and services that are not operated by us. This Privacy Policy does not apply to third-party websites. We are not responsible for the privacy practices of external websites. We recommend reviewing their privacy policies before providing your information.
          </p>
        </section>

        {/* Policy Updates */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Changes to This Privacy Policy</h2>

          <p className="text-gray-700 dark:text-gray-300">
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, and other factors. We will notify you of material changes by updating the "Last updated" date and, in some cases, by sending you an email notification. Your continued use of our services following the posting of changes constitutes your acceptance of the updated Privacy Policy.
          </p>
        </section>

        {/* Contact Section */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" />
            Contact Us
          </h2>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 p-8 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
            </p>

            <div className="space-y-4">
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">Nevxoaulio Ventures</p>
                <p className="text-gray-600 dark:text-gray-300">2nd Floor, Shylashree Nilaya, 2nd cross, Banashankari, Gandinagar Tumkur, Tumkur- 572102, India</p>
              </div>

              <div>
                <p className="font-semibold text-gray-900 dark:text-white mb-2">Email:</p>
                <a
                  href="mailto:privacy@nevxoaulio.com"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  privacy@nevxoaulio.com
                </a>
              </div>

              <div>
                <p className="font-semibold text-gray-900 dark:text-white mb-2">General Inquiries:</p>
                <a
                  href="mailto:info@nevxoaulio.com"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  info@nevxoaulio.com
                </a>
              </div>

              <div>
                <p className="font-semibold text-gray-900 dark:text-white mb-2">Phone:</p>
                <a
                  href="tel:+919449061503"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  +91 94490 61503
                </a>
              </div>

              <div>
                <p className="font-semibold text-gray-900 dark:text-white mb-2">WhatsApp:</p>
                <a
                  href="https://wa.me/919980031103"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  +91 99800 31103
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-8 mt-12">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            © {new Date().getFullYear()} Nevxoaulio Ventures. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;